import React, { useEffect, useRef, useState } from "react";
import { assetUrl } from "fusion-core";
import {
  downloadTOBA,
  uberDriverTerms,
} from "../../../shared/utils/apiUtils";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import Button from "../../../shared/components/Button";

const uberLogo = assetUrl("../../../assets/landing/uber/Uber_Logo_White_RGB.svg");
const validationMessage =
  "Tick the box to confirm you have read the Uber Driver Terms.";

const ConnectQuoteDialog = ({
  isOpen,
  onClose,
  onContinue,
  openerRef,
  navigate,
}) => {
  const dialogRef = useRef(null);
  const titleRef = useRef(null);
  const checkboxRef = useRef(null);
  const [termsRead, setTermsRead] = useState(false);
  const [error, setError] = useState("");
  const [fallbackOpen, setFallbackOpen] = useState(false);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;

    if (isOpen && !dialog.open) {
      setTermsRead(false);
      setError("");
      document.documentElement.classList.add("has-dialog");
      if (typeof dialog.showModal === "function") {
        try {
          dialog.showModal();
        } catch {
          openFallbackDialog(dialog);
        }
      } else {
        // Older embedded browsers do not implement the native dialog API.
        openFallbackDialog(dialog);
      }
      titleRef.current?.focus();
    }
  }, [isOpen]);

  const finishClose = () => {
    document.documentElement.classList.remove("has-dialog");
    setTermsRead(false);
    setError("");
    onClose();
    openerRef?.current?.focus();
  };

  const closeDialog = () => {
    const dialog = dialogRef.current;
    if (!dialog?.open) return;

    if (dialog.dataset.fallbackOpen === "true") {
      dialog.removeAttribute("open");
      delete dialog.dataset.fallbackOpen;
      setFallbackOpen(false);
      TrackHotjarEvent(HotjarEventNames.TAXI_LANDING_CONNECT_DIALOG_CLOSED);
      finishClose();
      return;
    }

    dialog.close();
  };

  const openFallbackDialog = (dialog) => {
    dialog.dataset.fallbackOpen = "true";
    dialog.setAttribute("open", "");
    setFallbackOpen(true);
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!termsRead) {
      TrackHotjarEvent(HotjarEventNames.TAXI_LANDING_CONNECT_VALIDATION_ERROR);
      setError("");
      window.setTimeout(() => setError(validationMessage), 50);
      checkboxRef.current?.setAttribute("aria-invalid", "true");
      checkboxRef.current?.focus();
      return;
    }

    TrackHotjarEvent(HotjarEventNames.TAXI_LANDING_CONNECT_CONTINUE_CLICKED);
    onContinue();
  };

  const handleDocumentLink = (event, download) => {
    event.preventDefault();
    download({ navigate });
  };

  return (
    <>
    {fallbackOpen && (
      <div
        className="tw-fixed tw-inset-0 tw-z-[1000] tw-bg-dis-scrim"
        aria-hidden="true"
        onClick={closeDialog}
      />
    )}
    <dialog
      ref={dialogRef}
      id="connect-dialog"
      className="tw-fixed tw-inset-x-0 tw-top-auto tw-bottom-0 tw-m-0 tw-max-h-[calc(100dvh-8px)] tw-w-full tw-max-w-full tw-overflow-hidden tw-rounded-t-[20px] tw-border-0 tw-bg-white tw-p-0 tw-text-dis-navy tw-shadow-dis-connect-mobile tw-text-[17px] tw-leading-[1.5]  open:tw-flex open:tw-flex-col open:tw-animate-connect-up data-[fallback-open=true]:tw-z-[1001] [&::backdrop]:tw-bg-dis-scrim [&[open]::backdrop]:tw-animate-connect-fade md:tw-inset-0 md:tw-m-auto md:tw-h-fit md:tw-w-[min(580px,calc(100vw-48px))] md:tw-max-h-[calc(100dvh-32px)] md:tw-rounded-[18px] md:tw-shadow-dis-connect-desktop md:open:tw-animate-connect-pop motion-reduce:open:tw-animate-none"
      role="dialog"
      aria-modal="true"
      aria-labelledby="connect-title"
      aria-describedby="connect-intro"
      onCancel={(event) => {
        event.preventDefault();
        closeDialog();
      }}
      onClose={() => {
        TrackHotjarEvent(HotjarEventNames.TAXI_LANDING_CONNECT_DIALOG_CLOSED);
        finishClose();
      }}
      onClick={(event) => {
        if (event.target === event.currentTarget) closeDialog();
      }}
    >
      <form className="tw-m-0 tw-flex tw-min-h-0 tw-flex-[1_1_auto] tw-flex-col" onSubmit={handleSubmit} noValidate>
        <div className="tw-relative tw-flex tw-min-h-[54px] tw-flex-[0_0_auto] tw-items-center tw-justify-center tw-border-x-0 tw-border-t-0 tw-border-b tw-border-solid tw-border-dis-border tw-px-[54px] tw-py-1.5 md:tw-min-h-[66px] md:tw-px-16 md:tw-py-2">
          <h2 id="connect-title" ref={titleRef} tabIndex="-1" className="tw-m-0 tw-text-center tw-text-lg tw-font-extrabold tw-leading-[1.2] tw-tracking-[-0.01em] focus:tw-outline-none md:tw-text-[22px]">
            Let’s get your quote started
          </h2>
          <button
            type="button"
            className="tw-absolute tw-appearance-none tw-border-solid tw-right-3 tw-top-0 tw-bottom-0 tw-my-auto tw-inline-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-rounded-full tw-border-0 tw-bg-white tw-p-0 tw-text-dis-navy tw-cursor-pointer  hover:tw-bg-dis-sky-tint focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
            onClick={closeDialog}
            aria-label="Close"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="m6 6 12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div className="tw-flex tw-min-h-0 tw-flex-[1_1_auto] tw-flex-col tw-gap-2.5 tw-overflow-y-auto tw-overscroll-contain tw-px-[18px] tw-pb-2.5 tw-pt-3.5 [&>*]:tw-shrink-0 md:tw-gap-4 md:tw-px-7 md:tw-pb-2.5 md:tw-pt-[22px] max-[379px]:tw-px-4 max-[379px]:tw-pb-2 max-[379px]:tw-pt-3">
          <div className="tw-grid tw-grid-cols-[48px_1fr] tw-items-start tw-gap-x-3.5 tw-gap-y-1 md:tw-grid-cols-[60px_1fr] md:tw-gap-x-4 md:tw-gap-y-1.5 max-[379px]:tw-grid-cols-[44px_1fr] max-[379px]:tw-gap-x-3">
            <span className="tw-row-span-2 tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-overflow-hidden tw-rounded-xl tw-bg-black md:tw-h-[60px] md:tw-w-[60px] md:tw-rounded-[14px] max-[379px]:tw-h-11 max-[379px]:tw-w-11" role="img" aria-label="Uber">
              <img className="tw-block tw-h-auto tw-w-[61px] tw-max-w-none tw-shrink-0 md:tw-w-[74px]" src={uberLogo} alt="" />
            </span>
            <h3 className="tw-m-0 tw-text-lg tw-font-extrabold tw-leading-[1.22] tw-tracking-[-0.01em] md:tw-text-[21px] max-[379px]:tw-text-[17px] ">Connect your Uber account for a quicker, easier insurance quote.</h3>
            <p id="connect-intro" className="tw-m-0 tw-text-[15px] tw-leading-[1.4] tw-text-dis-text-2 md:tw-text-base md:tw-leading-[1.45] max-[379px]:tw-text-[14.5px]">
              We’ll use your Uber driver information to help prepare your quote,
              saving you time entering details.
            </p>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2.5 tw-rounded-[14px] tw-bg-dis-info-panel tw-px-3.5 tw-py-3 md:tw-gap-3.5 md:tw-px-[18px] md:tw-py-4 max-[379px]:tw-p-3">
            <h4 className="tw-m-0 tw-text-base tw-font-extrabold tw-leading-[1.25] md:tw-text-[17px] ">Before you continue:</h4>
            <ul className="tw-m-0 tw-flex tw-list-none tw-flex-col tw-gap-2.5 tw-p-0 md:tw-gap-3.5">
              <li className="tw-flex tw-items-center tw-gap-3 md:tw-gap-4">
                <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-dis-success-bg tw-text-dis-success tw-shadow-dis-success-icon md:tw-h-[46px] md:tw-w-[46px]" aria-hidden="true">
                  <svg className="max-[379px]:tw-h-5 max-[379px]:tw-w-5" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                    <path d="M14 3H7.5A2.5 2.5 0 0 0 5 5.5v13A2.5 2.5 0 0 0 7.5 21h9a2.5 2.5 0 0 0 2.5-2.5V8z" />
                    <path d="M14 3v5h5M9 12.5h6M9 16.5h6" />
                  </svg>
                </span>
                <p className="tw-m-0 tw-text-[15px] tw-leading-[1.4] tw-text-dis-text-2 md:tw-text-[15.5px] md:tw-leading-[1.45] max-[379px]:tw-text-[14.5px]">
                  Please check your details are correct and up to date, and tell
                  us anything important about you, your driving or your business
                  that could affect your insurance.
                </p>
              </li>
              <li className="tw-flex tw-items-center tw-gap-3 md:tw-gap-4">
                <span className="tw-flex tw-h-10 tw-w-10 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-dis-info-icon-bg tw-text-dis-deep-blue tw-shadow-dis-info-icon md:tw-h-[46px] md:tw-w-[46px]" aria-hidden="true">
                  <svg className="max-[379px]:tw-h-5 max-[379px]:tw-w-5" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" focusable="false">
                    <path d="M12 3l7 3v5.5c0 4.5-3 8-7 9.5-4-1.5-7-5-7-9.5V6z" />
                    <path d="m9 12 2 2 4-4" />
                  </svg>
                </span>
                <p className="tw-m-0 tw-text-[15px] tw-leading-[1.4] tw-text-dis-text-2 md:tw-text-[15.5px] md:tw-leading-[1.45] max-[379px]:tw-text-[14.5px]">
                  This helps us provide an accurate quote and make sure you have
                  the right cover for your needs.
                </p>
              </li>
            </ul>
          </div>

          <p className="tw-m-0 tw-text-[15px] tw-leading-[1.4] tw-text-dis-text-2 md:tw-text-[15.5px] md:tw-leading-[1.45] max-[379px]:tw-text-[14.5px]">
            For more information, see our{" "}
            <a
              href="#terms-of-business"
              className="tw-text-dis-deep-blue tw-underline-offset-[3px] focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
              onClick={(event) => handleDocumentLink(event, downloadTOBA)}
            >
              Terms of <span className="tw-whitespace-nowrap">Business</span>
              <ExternalLinkIcon />
            </a>.
          </p>

          <p className={error ? "tw-border-l-4 tw-border-dis-error tw-pl-2.5 tw-text-[15px] tw-font-bold tw-text-dis-error" : "tw-hidden"} id="connect-error" role="alert">
            {error}
          </p>
          <label
            className={`tw-mx-[-10px] tw-mt-[-4px] tw-flex tw-min-h-11 tw-cursor-pointer tw-items-start tw-gap-3 tw-rounded-xl tw-border-2 tw-border-transparent tw-px-2.5 tw-py-2 tw-text-[15px] tw-leading-[1.4] tw-transition-colors hover:tw-bg-dis-info-hover md:tw-text-base md:tw-leading-[1.42] max-[379px]:tw-text-[14.5px] motion-reduce:tw-transition-none${error ? " tw-border-dis-error tw-bg-dis-error-bg hover:tw-bg-dis-error-bg" : ""}`}
            htmlFor="connect-agree"
          >
            <input
              ref={checkboxRef}
              type="checkbox"
              id="connect-agree"
              name="uber_terms_read"
              value="yes"
              className="tw-landing-native-checkbox tw-m-0 tw-mt-[-1px] tw-h-6 tw-w-6 tw-min-w-6 tw-flex-[0_0_24px] tw-cursor-pointer tw-accent-dis-deep-blue tw-p-0 tw-shadow-none focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2 focus-visible:tw-shadow-none [&:checked::after]:tw-content-none"
              checked={termsRead}
              aria-describedby="connect-error"
              aria-invalid={error ? "true" : undefined}
              onChange={(event) => {
                setTermsRead(event.target.checked);
                if (event.target.checked) setError("");
              }}
            />
            <span>
              I have read and understood the{" "}
              <a
                href="#uber-driver-terms"
                className="tw-text-dis-deep-blue tw-underline-offset-[3px] focus-visible:tw-outline-2 focus-visible:tw-outline-dis-deep-blue focus-visible:tw-outline-offset-2"
                onClick={(event) => handleDocumentLink(event, uberDriverTerms)}
              >
                Uber Driver Terms for Insurance <span className="tw-whitespace-nowrap">Arrangements</span>
                <ExternalLinkIcon />
              </a>.
            </span>
          </label>
        </div>

        <div className="tw-flex-[0_0_auto] tw-bg-white tw-px-[18px] tw-pb-[calc(14px+env(safe-area-inset-bottom))] tw-pt-1.5 md:tw-px-7 md:tw-pb-6 md:tw-pt-2.5 max-[379px]:tw-px-4 max-[379px]:tw-pb-[calc(12px+env(safe-area-inset-bottom))]">
          <Button type="submit" size="large" fullWidth data-track="connect-continue" className="max-[379px]:tw-text-[17px]">
            Continue to my quote
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </Button>
        </div>
      </form>
    </dialog>
    </>
  );
};

function ExternalLinkIcon() {
  return (
    <svg className="tw-ml-[3px] tw-inline-block tw-align-[-1px]" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M7 17 17 7M9 7h8v8" />
    </svg>
  );
}

export default ConnectQuoteDialog;
